import { useCallback, useEffect, useRef, useState } from "react";
import { TimingFunction, TIMING_FUNCTION } from "../utils/animation";

interface ProgressLoaderOptions {
    totalTime: number;
    timeStep: number;
    startVal: number;
    stopVal: number;
    maxVal: number;
    timingFunction: TimingFunction;
}

/**
 * Utility Hook to gradually produce a series of numbers as per provided timing function and animate the value transitions
 * @returns
 */
export default function useProgressLoader({
    totalTime,
    timeStep,
    startVal,
    stopVal,
    maxVal,
    timingFunction,
}: ProgressLoaderOptions) {
    const [value, setValue] = useState(0);
    const generatorRef = useRef<Generator<number>>(null);
    const timerRef = useRef(null);

    /**
     * Tick function to produce next value and schedule next update
     */
    const tick = useCallback(() => {
        if (generatorRef.current) {
            const { value, done } = generatorRef.current.next();
            if (!done) {
                setValue(value);
                timerRef.current = setTimeout(tick, timeStep);
            }
        }
    }, [setValue, timeStep]);

    /**
     * Starts the Progress Bar
     */
    const start = useCallback(() => {
        const timeInterval = totalTime / timeStep;
        generatorRef.current = seriesGenerator(
            timeInterval,
            startVal,
            stopVal,
            maxVal,
            timingFunction,
        );
        tick();
    }, [totalTime, timeStep, startVal, stopVal, maxVal, timingFunction, tick]);

    /**
     * Clear the timers
     */
    const clear = useCallback(() => {
        generatorRef.current = null;
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    /**
     * Finish the Progress till maxVal
     */
    const finish = useCallback(() => {
        setValue(maxVal);
        clear();
    }, [setValue, clear, maxVal]);

    /**
     * Reset the progress to initial value
     */
    const reset = useCallback(() => {
        setValue(0);
        clear();
    }, [setValue, clear]);

    /**
     * On-Unmount, clear stuff
     */
    useEffect(() => {
        return () => clear();
    }, [clear]);

    return {
        value,
        start,
        finish,
        reset,
    };
}

/**
 * Generator Function to produce a series of values from 0 to maxVal as per-provided timing function
 * @param interval
 * @param stopVal
 * @param maxVal
 * @param timingFunction
 * @returns
 */
function* seriesGenerator(
    interval: number,
    startVal: number,
    stopVal: number,
    maxVal: number,
    timingFunction: TimingFunction,
) {
    const stepIncrement = (stopVal - startVal) / interval;
    const fn = TIMING_FUNCTION[timingFunction];

    if (!fn) {
        return;
    }

    for (let x = startVal; ; x += stepIncrement) {
        const y = fn(x / maxVal) * maxVal;
        yield y;
        if (y >= stopVal) {
            break;
        }
    }
}
